
import Vue from 'vue';
import { DEACTIVATE_ENDPOINT, REACTIVATE_ENDPOINT } from '@/gql';
import { mapGetters } from 'vuex';
import gql from 'graphql-tag';
import Logging from '@/logging';
import clip from '@/utils/clipboard.js';
import ComponentsContacts_userInterfaceDetails from './user-interface-details.graphql';
import CustomFieldsList from '@/components/_CustomFields/CustomFieldsList.vue';
import ContactDialog from '@/components/_dialogs/ContactDialog.vue';
import ChannelEndpointDialog from '@/components/_dialogs/ChannelEndpointDialog.vue';
import axios from 'axios';
import { hasLicenseAndPermission } from '@/helpers/permission-helper';

export default Vue.extend({
	apollo: {
		userInterfaceDetails: {
			query: ComponentsContacts_userInterfaceDetails,
			variables() {
				return {
					participant: this.selectedParticipantStore
				};
			},
			update: data => data.userInterfaceDetails[0]
		}
	},
	components: {
		ChannelEndpointDialog,
		ContactDialog,
		CustomFieldsList
	},
	props: {
		recipient: {
			type: String,
			required: true
		},
		legalEntity: {
			type: String,
			required: true
		},
		preSelected: {
			type: Function,
			required: false,
			default: null
		},
		selectable: {
			type: Boolean,
			required: false,
			default: () => false
		},
		endpointSelectable: {
			type: Boolean,
			required: false,
			default: () => false
		},
		disabled: {
			type: Function,
			required: false,
			default: null
		},
		onSelect: {
			type: Function,
			required: false,
			default: null
		},
		onSelectEndpoint: {
			type: Function,
			required: false,
			default: null
		}
	},
	data() {
		return {
			contacts: undefined,
			totalContacts: 0,
			loading: false,
			channelEndpointDialogContact: null,
			channelEndpointDialogEndpoint: null,
			contactDialogVisible: false,
			contactDialogContact: null,
			handleRemoveContactLoading: false,
			handleRemoveChannelEndpointLoading: false
		};
	},
	computed: {
		...mapGetters(['env', 'selectedParticipantStore', 'selectedContact', 'loggedInUser']),
		allowEdit() {
			const licences = this.$store.state.risolto.licences[this.selectedParticipantStore];
			const permissions = this.$store.state.risolto.permissions[this.selectedParticipantStore];
			return hasLicenseAndPermission(
				licences,
				permissions,
				'CORE',
				'UI_EDIT_CONTACTS'
			);
		},
		deactivedButUsedEndpoints() {
			if (this.contacts) {
				var endpoints = this.contacts
					.flatMap(c => c.deactivated_but_used_endpoints);
				return [...new Set(endpoints)];
			} else {
				return [];
			}
		}
	},
	watch: {
		'$store.state.apollo.risolto_contact': function() {
			this.getContacts();
		},
		'$store.state.apollo.risolto_channel_endpoint': function() {
			this.getContacts();
		},
		recipient() {
			this.getContacts();
		},
		contacts() {
			if (this.preSelected) {
				let preSelectedContact = null;
				this.contacts.forEach(contact => {
					if (this.preSelected(contact)) {
						preSelectedContact = contact;
					}
				});
				this.$store.commit('risolto/SET_SELECTED_CONTACT', preSelectedContact);
			} else {
				this.$store.commit('risolto/SET_SELECTED_CONTACT', null);
			}
		}
	},
	created() {
		this.getContacts();
	},
	methods: {
		handleSelectContact(contact, channelEndpoint) {
			if (typeof this.onSelect === 'function') {
				this.onSelect(contact, channelEndpoint);
			}
		},
		handleDeactivate(reference) {
			this.$apollo
				.mutate({
					mutation: DEACTIVATE_ENDPOINT,
					variables: {
						participant: this.selectedParticipantStore,
						deactivated_by: this.loggedInUser.reference,
						reference: reference
					}
				})
				.then(result => {
					Logging.log('DEACTIVATE_ENDPOINT: ', result);
					this.$store.commit('apollo/RISOLTO_CONTACT');
					this.$notify({
						title: this.$t('success'),
						message: this.$t('endpointDeactivated'),
						type: 'success'
					});
				})
				.catch(e => {
					Logging.error('DEACTIVATE_ENDPOINT: ', e);
				});
		},
		handleReactivate(endpoint) {
			// Check if endpoint was deactivated by blacklist check
			if (endpoint.deactivated_by === 'blacklist_check') {
				this.$notify({
					title: this.$t('error.cannotReactivateBlacklistEndpoint'),
					message: this.$t('cannotReactivateBlacklistEndpoint'),
					type: 'error',
					duration: 5000
				});
				return;
			}

			this.$apollo
				.mutate({
					mutation: REACTIVATE_ENDPOINT,
					variables: {
						participant: this.selectedParticipantStore,
						reference: endpoint.reference
					}
				})
				.then(result => {
					Logging.log('REACTIVATE_ENDPOINT: ', result);
					this.$store.commit('apollo/RISOLTO_CONTACT');
					this.$notify({
						title: this.$t('success'),
						message: this.$t('endpointActivated'),
						type: 'success'
					});
				})
				.catch(e => {
					Logging.error('REACTIVATE_ENDPOINT: ', e);
				});
		},
		handleCopy(text, event) {
			event.stopPropagation();
			clip(text, event);
		},
		handlSelectedEndpoint(contact, endpoint, event) {
			event.stopPropagation();
			if (typeof this.onSelectEndpoint === 'function') {
				this.onSelectEndpoint(contact, endpoint);
			}
		},
		handleAddContact() {
			this.contactDialogVisible = true;
		},
		handleEditContact(contact) {
			this.contactDialogContact = contact;
			this.contactDialogVisible = true;
		},
		async handleRemoveContact(contact) {
			if (this.handleRemoveContactLoading) {
				return;
			}

			this.handleRemoveContactLoading = true;
			const payload = {
				participant: this.selectedParticipantStore,
				legal_entity: this.legalEntity,
				contact: contact
			};

			try {
				await axios.delete(`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/contact`, {
					data: payload,
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
				this.$store.commit('apollo/RISOLTO_CONTACT');
			} catch (e) {
				//
			} finally {
				this.handleRemoveContactLoading = false;
			}
		},
		handleAddChannelEndpoint(contact) {
			this.channelEndpointDialogContact = contact;
			this.channelEndpointDialogEndpoint = null;
		},
		handleEditChannelEndpoint(contact, endpoint) {
			this.channelEndpointDialogContact = contact;
			this.channelEndpointDialogEndpoint = endpoint;
		},
		async handleRemoveChannelEndpoint(endpoint) {
			if (this.handleRemoveChannelEndpointLoading) {
				return;
			}

			this.handleRemoveChannelEndpointLoading = true;
			const payload = {
				participant: this.selectedParticipantStore,
				legal_entity: this.legalEntity,
				endpoint: endpoint
			};

			try {
				await axios.delete(`https://${process.env.VUE_APP_API_ENDPOINT}/operator-fe/channel-endpoint`, {
					data: payload,
					headers: { Authorization: `Bearer ${localStorage.getItem('rslt_id_token')}` }
				});
				this.$store.commit('apollo/RISOLTO_CHANNEL_ENDPOINT');
			} catch (e) {
				//
			} finally {
				this.handleRemoveChannelEndpointLoading = false;
			}
		},
		getContacts() {
			this.loading = true;

			const vars = {
				participant: this.selectedParticipantStore,
				recipient: this.recipient
			};

			Logging.log('Contacts.getContacts');

			this.$apollo
				.query({
					query: this.contactsQuery(),
					variables: vars
				})
				.then(result => {
					Logging.log('Contacts.getContacts - data: ', result.data);
					this.loading = false;
					if (result.data) {
						this.contacts = result.data.contacts;

						for (let contact of result.data.contacts) {
							let channel_endpoints = contact.channel_endpoints
								.map(el => {
									if (el.channel === 'PHONE' || el.channel === 'EMAIL' || el.channel === 'MAIL') { // TODO: PEPPOL: I don't understand why there is this if and a nested if?
										if (el.channel === 'PHONE') el.icon = 'phone-solid';
										if (el.channel === 'EMAIL') el.icon = 'at-solid';
										if (el.channel === 'MAIL') el.icon = 'envelope-solid';
										if (el.channel === 'PEPPOL') el.icon = 'envelope-solid'; // TODO: PEPPOL icon, e.g. electronic-receipt
										return el;
									}
								});

							contact.channel_endpoints = channel_endpoints;
							let activeEndpoints = contact.channel_endpoints
								.filter(c => c.deactivated_at == null);
							contact.active_endpoints = activeEndpoints;
							let deactivatedButUsedEndpoints = contact.channel_endpoints
								.filter(c => c.deactivated_at != null && c.in_action === true);
							contact.deactivated_but_used_endpoints = deactivatedButUsedEndpoints;
						}
					}
				})
				.catch(e => {
					Logging.error('Error get contacts: ', e);
					this.loading = false;
				});
		},
		contactsQuery() {
			return gql`
	query componentsContacts_getContacts($recipient: uuid!, $participant: uuid!) {
		total: risolto_contact_aggregate(where: { 
			_and: [
				{ recipient: { _eq: $recipient } }
				{ participant: { _eq: $participant } }
				{ deactivated_at: { _is_null: true } }
			] 
		}) {
			aggregate {
				count
			}
		}
		contacts: risolto_contact(where: { 
			_and: [
				{ recipient: { _eq: $recipient } }
				{ participant: { _eq: $participant } } 
				{ deactivated_at: { _is_null: true } }
			]
		}) {
			legal_entity
			locale
			participant
			first_name
			last_name
			full_name
			reference
			recipient
			contact_type_by_reference {
				reference
				contact_type
			}
			channel_endpoints(
				where: {
					channel: { _neq: "SMS" }
				}
			) {
				in_action
				endpoint_details
				deactivated_at
				deactivated_by
				channel
				endpoint
				reference
				origin
				locale
			}
			custom_fields
			origin
		}
	}
`;
		}
	}
});
